import { Handler, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { updateBillTo } from "@insite/client-framework/Services/CustomersService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { chain } from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { UpdateBillTo } from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { BaseAddressModel, BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import cloneDeep from "lodash/cloneDeep";

type HandlerType = Handler<
    HasOnSuccess,
    {
        shipTo?: ShipToModel;
        updatedBillTo?: BillToModel;
        updatedShipTo?: ShipToModel;
        cart: Cart;
        apiParameter: UpdateCartApiParameter;
        apiResult: CartResult;
    }
>;

function areAddressesTheSame(address1: BaseAddressModel, address2: BaseAddressModel) {
    return (
        address1.firstName === address2.firstName &&
        address1.lastName === address2.lastName &&
        address1.attention === address2.attention &&
        address1.companyName === address2.companyName &&
        address1.address1 === address2.address1 &&
        address1.address2 === address2.address2 &&
        address1.address3 === address2.address3 &&
        address1.address4 === address2.address4 &&
        address1.country?.id === address2.country?.id &&
        address1.city === address2.city &&
        address1.state?.id === address2.state?.id &&
        address1.postalCode === address2.postalCode &&
        address1.phone === address2.phone &&
        address1.email === address2.email &&
        address1.fax === address2.fax
    );
}

export const UpdateBillToExtended: HandlerType = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const {
        pages: {
            checkoutShipping: { billingAddressFormState },
        },
    } = state;
    if (!billingAddressFormState) {
        return;
    }

    const billTo = getBillToState(state, cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedBillTo } = billingAddressFormState;
    if (!areAddressesTheSame(billTo, localModifiedBillTo)) {
        const tempProps = cloneDeep(localModifiedBillTo);
        tempProps.properties["isBillToUpdatedFromCart"] = "true";
        Object.preventExtensions(tempProps);
        props.updatedBillTo = await updateBillTo({
            billTo: tempProps,
            isEdit: false,
        });
        dispatch(loadBillTo({ billToId: billTo.id }));
        if (cart.shipToId === billTo.id) {
            dispatch(loadShipTo({ shipToId: cart.shipToId, billToId: billTo.id }));
        }
        props.apiParameter.cart.billToId = props.updatedBillTo.id;
    }
};

replaceInChain(chain, UpdateBillTo, UpdateBillToExtended);
